import React from 'react';
import { getPrinterConfig } from '../../hooks/usePrinterConfig';

export interface OrderTicketItem {
  qty: number;
  name: string;
  notes?: string;
  extras?: string[]; // sabores, adiciones, etc.
}

export interface OrderTicketData {
  title: string;              // "MESA 1" / "PARA LLEVAR"
  orderNumber?: string | number;
  time?: string;             // ISO o ya formateada
  items: OrderTicketItem[];
}

/**
 * Comanda de cocina optimizada para impresora térmica de 58/80mm.
 * Prioriza LEGIBILIDAD: nombres y cantidades grandes y en negrita.
 */
export const OrderTicket = React.forwardRef<HTMLDivElement, { data: OrderTicketData }>(
  ({ data }, ref) => {
    const width = getPrinterConfig().paperWidth;
    const big = width <= 58;
    // Tamaños grandes para que la cocina lea de lejos
    const nameSize = big ? 19 : 22;
    const qtySize = big ? 22 : 26;
    const headerSize = big ? 20 : 24;
    const metaSize = big ? 12 : 13;
    const extraSize = big ? 13 : 15;

    const time = data.time ? new Date(data.time) : new Date();
    const timeStr = isNaN(time.getTime()) ? '' : time.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
    const totalUnits = data.items.reduce((s, i) => s + (i.qty || 1), 0);

    return (
      <div
        ref={ref}
        data-testid="order-ticket"
        style={{
          width: `${width}mm`,
          fontFamily: '"Courier New", Courier, monospace',
          color: '#000',
          background: 'white',
          padding: big ? '6px 6px' : '8px 10px',
          lineHeight: 1.25,
        }}
      >
        {/* Encabezado */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: 4, marginBottom: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>COMANDA</div>
          <div style={{ fontSize: headerSize, fontWeight: 800 }}>{data.title}</div>
          {data.orderNumber != null && (
            <div style={{ fontSize: metaSize }}>Pedido #{data.orderNumber}</div>
          )}
          {timeStr && <div style={{ fontSize: metaSize }}>{timeStr}</div>}
        </div>

        {/* Ítems */}
        <div>
          {data.items.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px dashed #999' }}>
              <span style={{ fontSize: qtySize, fontWeight: 800, minWidth: big ? 34 : 42, textAlign: 'right' }}>
                {it.qty || 1}×
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: nameSize, fontWeight: 800, textTransform: 'uppercase', wordBreak: 'break-word' }}>
                  {it.name}
                </div>
                {it.extras && it.extras.length > 0 && (
                  <div style={{ fontSize: extraSize, fontWeight: 600 }}>
                    {it.extras.map((e, k) => <div key={k}>• {e}</div>)}
                  </div>
                )}
                {it.notes && (
                  <div style={{ fontSize: extraSize, fontStyle: 'italic' }}>» {it.notes}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pie */}
        <div style={{ textAlign: 'center', marginTop: 6, fontSize: metaSize, fontWeight: 700 }}>
          {totalUnits} producto{totalUnits === 1 ? '' : 's'} en total
        </div>
        <div style={{ height: 24 }} />
      </div>
    );
  }
);

OrderTicket.displayName = 'OrderTicket';
export default OrderTicket;
